<template>
    <div class="order-sum summary-details">
        <div class="detail__list">
            <div class="detail__entry order-sum__entry">
                <div class="order-sum__label">Gesamtpreis (ohne Versand)</div>
                <div class="order-sum__value">
                    <template v-if="discountApplied">
                        <s class="is-5 has-text-danger is-size-6 is-block">
                            {{
                                (isBusinessCustomer
                                    ? positionPrice.beforeDiscount.netPrice
                                    : positionPrice.beforeDiscount.totalPrice)
                                    | toCents
                                    | currency
                            }}</s
                        >
                    </template>
                    <template>
                        <h3 class="property__value is-5">
                            <template v-if="cartSum">
                                {{
                                    cartSum.netPrice | toCents | currency
                                }} </template
                            ><template v-else>
                                {{
                                    (isBusinessCustomer
                                        ? positionPrice.netPrice
                                        : positionPrice.totalPrice)
                                        | toCents
                                        | currency
                                }}
                            </template>
                        </h3>
                    </template>
                </div>
            </div>
            <div
                v-if="smallOrderSurcharge && smallOrderSurcharge.netPrice > 0"
                class="detail__entry order-sum__entry"
            >
                <div class="order-sum__label">Mindermengenzuschlag</div>
                <div class="order-sum__value">
                    <h3 class="property__value is-5">
                        {{
                            (isBusinessCustomer
                                ? smallOrderSurcharge.netPrice
                                : smallOrderSurcharge.totalPrice)
                                | toCents
                                | currency
                        }}
                    </h3>
                </div>
            </div>
            <div class="detail__entry order-sum__entry">
                <div class="order-sum__label">Versandkosten</div>
                <div class="order-sum__value">
                    {{
                        (isBusinessCustomer
                            ? shippingMethod.pricing.netPrice
                            : shippingMethod.pricing.totalPrice) | currency
                    }}
                </div>
            </div>
            <div
                v-if="
                    (isBusinessCustomer && taxRate > 0) || $route.params.orderId
                "
                class="detail__entry order-sum__entry"
            >
                <div class="cart-sum__label">
                    <div>
                        <span>{{
                            $t('checkout.cart.plusXPercentTaxes', { taxRate })
                        }}</span>
                    </div>
                </div>
                <div class="cart-sum__value" v-if="orderSum">
                    +{{
                        orderSum.tax ||
                        orderSum.calculatedTaxes.tax | toCents | currency
                    }}
                </div>
            </div>
            <div
                class="detail__entry order-sum__entry--primary has-background-primary-dark has-text-light section mt-2"
            >
                <div class="order-sum__label">
                    <span class="title is-4 has-text-white"
                        >Zahlungsbetrag Gesamt</span
                    >
                    <span v-if="taxRate === 0">
                        {{ $t('checkout.cart.noTaxIncluded') }}
                    </span>
                    <span
                        v-else-if="!isBusinessCustomer || $route.params.orderId"
                        >{{
                            $t('checkout.cart.includingXPercentTaxes', {
                                taxRate,
                            })
                        }}</span
                    >
                </div>
                <strong
                    class="order-sum__value title is-4 has-text-white"
                    v-if="orderSum"
                >
                    <template v-if="discountApplied && orderSum.beforeDiscount">
                        <s
                            class="is-5 has-text-danger is-size-6 is-block mb-1 has-text-right"
                            >{{
                                orderSum.beforeDiscount.totalPrice
                                    | toCents
                                    | currency
                            }}</s
                        >
                    </template>
                    <template>
                        <div class="order-sum__value is-5" v-if="orderSum">
                            {{ orderSum.totalPrice | toCents | currency }}
                        </div>
                    </template>
                </strong>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { discountMixin } from '@/mixins/discountMixin';
import cart from '../../../store/modules/cart';
import { calculateTaxes } from '../../../utilities/tax-calculations';

export default {
    name: 'OrderSum',
    mixins: [discountMixin],
    data() {
        return {
            minimumOrderValue: 30,
        };
    },

    computed: {
        ...mapGetters([
            'positionPrice',
            'paymentDetails',
            'taxRate',
            'isBusinessCustomer',
            'discount',
            'discountApplied',
            'orderTrackingInfo',
        ]),
        shippingMethod() {
            let shippingMethod;
            if (
                this.orderTrackingInfo.shippingMethod &&
                this.orderTrackingInfo.shippingCosts
            ) {
                shippingMethod = {
                    ...this.orderTrackingInfo.shippingMethod,
                    pricing: this.orderTrackingInfo.shippingCosts,
                    priceCents:
                        this.orderTrackingInfo.shippingCosts.calculatedTaxes
                            .price * 100,
                };
            } else {
                shippingMethod = this.$store.getters
                    .shippingCountryById(
                        this.paymentDetails.shippingAddress.countryId
                    )
                    .shippingMethods.find(
                        (method) =>
                            method.shippingMethodId ===
                            this.paymentDetails.shippingMethodId
                    );
            }
            let price = shippingMethod.priceCents;
            if (
                shippingMethod.shippingMethod === 'Standard-Versand' &&
                this.discountApplied &&
                this.discount.rules.freeShipping
            ) {
                price = 0;
            }
            return {
                ...shippingMethod,
                pricing: calculateTaxes(price, this.taxRate),
            };
        },
        smallOrderSurcharge() {
            if (this.orderTrackingInfo.smallOrderSurcharge) {
                return this.orderTrackingInfo.smallOrderSurcharge;
            }
            let minimumOrderValue =
                this.minimumOrderValue / ((100 + this.taxRate) / 100);
            return calculateTaxes(
                minimumOrderValue - this.positionPrice.netPrice,
                this.taxRate
            );
        },
        cartSum() {
            if (this.orderTrackingInfo.price) {
                let netPrice =
                    this.orderTrackingInfo.price.positionPrice *
                    (1 / this.orderTrackingInfo.price.calculatedTaxes.taxRate);
                return calculateTaxes(
                    this.orderTrackingInfo.price.positionPrice,
                    this.orderTrackingInfo.price.calculatedTaxes.taxRate
                );
            }
            return null;
        },
        orderSum() {
            if (this.orderTrackingInfo && this.orderTrackingInfo.price) {
                return this.orderTrackingInfo.price;
            }
            let minimumOrderValue =
                this.minimumOrderValue / ((100 + this.taxRate) / 100);
            let positionPrice = Math.max(
                this.positionPrice.netPrice,
                minimumOrderValue
            );
            let smallOrderSurcharge = calculateTaxes(
                minimumOrderValue - this.positionPrice.netPrice,
                this.taxRate
            );

            let orderSum = calculateTaxes(
                positionPrice + this.shippingMethod.pricing.netPrice / 100,
                this.taxRate
            );
            if (this.discountApplied && positionPrice !== minimumOrderValue) {
                orderSum.beforeDiscount = calculateTaxes(
                    this.positionPrice.beforeDiscount.netPrice +
                        this.shippingMethod.pricing.netPrice / 100,
                    this.taxRate
                );
            }
            return orderSum;
        },
    },
};
</script>

<style scoped></style>
